import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { BookOpen } from "lucide-react";
import { cn, formatLibraryName } from "@/lib/utils";
import { useTenant } from "@/contexts/TenantContext";

interface AuthLayoutProps {
  children: React.ReactNode;
  headline: string;
  subtext: string;
  illustration?: React.ReactNode;
  maxWidth?: "md" | "lg" | "xl" | "2xl";
  brandPanelWidth?: "narrow" | "default" | "wide";
  verticalAlign?: "center" | "top";
}

const DEFAULT_LIBRARY_IMAGES = [
  "https://images.unsplash.com/photo-1507842217343-583bb7270b66?auto=format&fit=crop&w=1920&q=80",
  "https://images.unsplash.com/photo-1521587760476-6c12a4b040da?auto=format&fit=crop&w=1920&q=80",
  "https://images.unsplash.com/photo-1481627834876-b7833e8f5570?auto=format&fit=crop&w=1920&q=80",
  "https://images.unsplash.com/photo-1524995997946-a1c2e315a42f?auto=format&fit=crop&w=1920&q=80",
];

const maxWidthClasses = {
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
  "2xl": "max-w-2xl",
};

function DefaultIllustration() {
  return (
    <svg
      viewBox="0 0 400 300"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="w-full max-w-sm opacity-30"
      aria-hidden="true"
    >
      <rect x="60" y="180" width="280" height="8" rx="4" fill="white" />
      <rect x="80" y="200" width="240" height="8" rx="4" fill="white" />
      <rect x="100" y="220" width="200" height="8" rx="4" fill="white" />
      <rect x="60" y="60" width="40" height="100" rx="4" fill="white" />
      <rect x="110" y="40" width="40" height="120" rx="4" fill="white" />
      <rect x="160" y="70" width="40" height="90" rx="4" fill="white" />
      <rect x="210" y="50" width="40" height="110" rx="4" fill="white" />
      <rect x="260" y="80" width="40" height="80" rx="4" fill="white" />
      <rect x="310" y="55" width="40" height="105" rx="4" fill="white" />
      <rect x="45" y="160" width="310" height="4" rx="2" fill="white" />
    </svg>
  );
}

// ─── Slide Indicators ───────────────────────────────────────────────────

function SlideIndicators({ total, current }: { total: number; current: number }) {
  return (
    <div className="flex items-center gap-1.5" aria-hidden="true">
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          className={cn(
            "h-1 rounded-full transition-all duration-500",
            i === current ? "w-6 bg-white/80" : "w-1.5 bg-white/30"
          )}
        />
      ))}
    </div>
  );
}

// ─── Tenant Logo ────────────────────────────────────────────────────────

function TenantLogo({
  logoUrl,
  tenantName,
  variant = "light",
}: {
  logoUrl?: string | null;
  tenantName?: string;
  variant?: "light" | "dark";
}) {
  if (logoUrl) {
    return (
      <Link
        to="/"
        className="flex items-center gap-2.5 hover:opacity-90 transition-opacity shrink-0"
      >
        <img
          src={logoUrl}
          alt={tenantName || "Library"}
          className="h-8 w-auto max-w-[180px] object-contain"
        />
      </Link>
    );
  }

  return (
    <Link
      to="/"
      className={cn(
        "flex items-center gap-2.5 hover:opacity-90 transition-opacity shrink-0",
        variant === "light" ? "text-white" : "text-slate-900"
      )}
    >
      <BookOpen
        className={cn("h-7 w-7", variant === "light" ? "" : "text-primary")}
        strokeWidth={2.5}
      />
      <span className="text-lg font-heading font-bold tracking-tight">
        {formatLibraryName(tenantName || "Library")}
      </span>
    </Link>
  );
}

// ─── Desktop Image Slider ───────────────────────────────────────────────

function TenantSlider({ currentSlide, images }: { currentSlide: number; images: string[] }) {
  const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set([0]));

  useEffect(() => {
    const next = (currentSlide + 1) % images.length;
    if (!loadedImages.has(next)) {
      const img = new Image();
      img.src = images[next];
      img.onload = () => setLoadedImages((prev) => new Set(prev).add(next));
    }
  }, [currentSlide, images, loadedImages]);

  return (
    <div className="absolute inset-0" aria-hidden="true">
      {images.map((src, i) => (
        <div
          key={i}
          className={cn(
            "absolute inset-0 transition-opacity duration-[1500ms] ease-in-out",
            i === currentSlide ? "opacity-100" : "opacity-0"
          )}
        >
          <img
            src={src}
            alt=""
            className="w-full h-full object-cover"
            loading={i === 0 ? "eager" : "lazy"}
          />
        </div>
      ))}
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/65" />
    </div>
  );
}

// ─── Mobile Background ──────────────────────────────────────────────────

function MobileBackground({ image }: { image: string }) {
  return (
    <div className="absolute inset-0 lg:hidden" aria-hidden="true">
      <img src={image} alt="" className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-slate-900/70" />
    </div>
  );
}

// ─── Main Layout ────────────────────────────────────────────────────────

export default function AuthLayout({
  children,
  headline,
  subtext,
  illustration,
  maxWidth = "md",
  brandPanelWidth = "default",
  verticalAlign = "center",
}: AuthLayoutProps) {
  const { isMainPlatform, tenant } = useTenant();
  const isTenantDomain = !isMainPlatform;
  const tenantLogoUrl = tenant?.logoUrl ?? null;
  const tenantName = tenant?.name;

  const sliderImages = useMemo(() => {
    const custom = tenant?.authImages ?? tenant?.settings?.branding?.authImages;
    return custom?.length ? custom : DEFAULT_LIBRARY_IMAGES;
  }, [tenant?.authImages, tenant?.settings?.branding?.authImages]);

  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    if (!isTenantDomain || sliderImages.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % sliderImages.length);
    }, 7000);
    return () => clearInterval(timer);
  }, [isTenantDomain, sliderImages.length]);

  const panelWidthClass =
    brandPanelWidth === "narrow"
      ? "lg:w-[38%]"
      : brandPanelWidth === "wide"
        ? "lg:w-[50%]"
        : "lg:w-[45%]";

  return (
    <div className="min-h-screen flex">
      {/* ── Brand Panel — desktop only ─────────────────────── */}
      <div
        className={cn(
          "hidden lg:flex relative sticky top-0 h-screen self-start overflow-hidden",
          panelWidthClass,
          !isTenantDomain && "bg-gradient-to-br from-primary via-primary/95 to-primary/90"
        )}
      >
        {isTenantDomain && <TenantSlider currentSlide={currentSlide} images={sliderImages} />}

        <div className="relative z-10 flex flex-col h-full w-full p-12">
          {/* Logo */}
          {isTenantDomain ? (
            <TenantLogo logoUrl={tenantLogoUrl} tenantName={tenantName} />
          ) : (
            <Link
              to="/"
              className="flex items-center gap-2.5 text-white hover:opacity-90 transition-opacity shrink-0"
            >
              <BookOpen className="h-7 w-7" strokeWidth={2.5} />
              <span className="text-lg font-heading font-bold tracking-tight">PhiloLMS</span>
            </Link>
          )}

          {/* Center content */}
          <div className="flex-1 min-h-0 flex flex-col items-start justify-center max-w-md mt-10">
            <h1
              className={cn(
                "text-3xl xl:text-4xl font-heading font-bold leading-tight tracking-tight shrink-0",
                isTenantDomain ? "text-white drop-shadow-lg" : "text-white"
              )}
            >
              {headline}
            </h1>
            <p
              className={cn(
                "mt-4 text-base leading-relaxed shrink-0",
                isTenantDomain ? "text-white/80 drop-shadow-md" : "text-white/80"
              )}
            >
              {subtext}
            </p>

            {/* Illustration — only on main platform */}
            {!isTenantDomain && (
              <div className="mt-8 w-full overflow-hidden shrink">
                {illustration || <DefaultIllustration />}
              </div>
            )}

            {/* Slide indicators — only on tenant domain */}
            {isTenantDomain && sliderImages.length > 1 && (
              <div className="mt-8">
                <SlideIndicators total={sliderImages.length} current={currentSlide} />
              </div>
            )}
          </div>

          {/* Footer */}
          <p
            className={cn(
              "text-sm shrink-0 pt-6",
              isTenantDomain ? "text-white/50" : "text-white/50"
            )}
          >
            &copy; {new Date().getFullYear()}{" "}
            {isTenantDomain ? formatLibraryName(tenantName || "Library") : "PhiloLMS"}. All rights
            reserved.
          </p>
        </div>

        {/* Background decoration — main platform only */}
        {!isTenantDomain && (
          <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
            <div className="absolute -top-24 -right-24 w-96 h-96 bg-white/5 rounded-full" />
            <div className="absolute -bottom-32 -left-32 w-[500px] h-[500px] bg-white/5 rounded-full" />
            <div className="absolute top-1/2 right-0 w-64 h-64 bg-white/[0.03] rounded-full transform -translate-y-1/2 translate-x-1/2" />
          </div>
        )}
      </div>

      {/* ── Form Panel ─────────────────────────────────────── */}
      <div className="flex-1 flex flex-col relative">
        {/* Mobile background — tenant domains only, visible below lg */}
        {isTenantDomain && <MobileBackground image={sliderImages[0]} />}

        {/* Main platform keeps solid white background */}
        {!isTenantDomain && <div className="absolute inset-0 bg-white" aria-hidden="true" />}

        {/* Mobile header */}
        <header
          className={cn(
            "relative z-10 lg:hidden px-6 py-5",
            isTenantDomain ? "border-b border-white/10" : "border-b border-slate-100/80"
          )}
        >
          {isTenantDomain ? (
            <TenantLogo logoUrl={tenantLogoUrl} tenantName={tenantName} variant="light" />
          ) : (
            <Link
              to="/"
              className="flex items-center gap-2 text-slate-900 hover:opacity-80 transition-opacity"
            >
              <BookOpen className="h-6 w-6 text-primary" />
              <span className="text-base font-heading font-bold">PhiloLMS</span>
            </Link>
          )}
        </header>

        {/* Form content */}
        <div
          className={cn(
            "relative z-10 flex-1 flex justify-center px-6 py-10 lg:px-10 lg:py-12 overflow-y-auto",
            verticalAlign === "center" ? "items-center" : "items-start"
          )}
        >
          <div className={cn("w-full", maxWidthClasses[maxWidth])}>
            {/* On mobile tenant domains, wrap form in a card for contrast */}
            {isTenantDomain ? (
              <div className="lg:bg-transparent lg:shadow-none lg:p-0 bg-white rounded-2xl shadow-xl shadow-black/10 p-6 sm:p-8 border border-white/20 lg:border-0">
                {children}
              </div>
            ) : (
              children
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
